import { useIntl } from "react-intl";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PersonTableColumns } from "~pages/Persons/PersonListPage/enums/PersonTableColumns.ts";
import { SeasonTableColumns } from "~pages/Seasons/SeasonListPage/enums/SeasonTableColumns.ts";
import type { Image } from "~types/image.ts";
import type { Person } from "~types/person.ts";

export const useGetPersonTableColumns = (): Array<DataTableColumn<Person>> => {
  const { formatMessage } = useIntl();

  return [
    {
      key: SeasonTableColumns.Image,
      title: formatMessage({ id: "persons.table.column.image" }),
      dataIndex: "images",
      width: 25,
      render: (value: Array<Image>) => {
        const logo = value && value.length > 0 ? value.find((image) => image.imageType === ImageTypes.PersonHead) : null;
        return logo ? (
          <img alt="Person logo" src={`${logo.url}?size=200`} width={25} height={25} />
        ) : (
          <i style={{ width: 25 }} className="fas fa-shield fa-xl" />
        );
      },
    },
    {
      key: PersonTableColumns.PersonId,
      title: formatMessage({ id: "persons.table.column.personId" }),
      dataIndex: "personId",
      width: 300,
    },
    {
      key: PersonTableColumns.ExternalId,
      title: formatMessage({ id: "persons.table.column.externalId" }),
      dataIndex: "externalId",
      width: 300,
    },
    {
      key: PersonTableColumns.NameFullLocal,
      title: formatMessage({ id: "persons.table.column.nameFullLocal" }),
      dataIndex: "nameFullLocal",
      sorter: true,
    },
    {
      key: PersonTableColumns.NameFullLatin,
      title: formatMessage({ id: "persons.table.column.nameFullLatin" }),
      dataIndex: "nameFullLatin",
      width: 150,
      sorter: true,
    },
    {
      key: PersonTableColumns.KnownAs,
      title: formatMessage({ id: "persons.table.column.knownAs" }),
      dataIndex: ["additionalNames", "default", "knownAs"],
      width: 150,
    },
    {
      key: PersonTableColumns.NameGivenLocal,
      title: formatMessage({ id: "persons.table.column.nameGivenLocal" }),
      dataIndex: ["nameGivenLocal"],
      width: 150,
    },
    {
      key: PersonTableColumns.NameGivenLatin,
      title: formatMessage({ id: "persons.table.column.nameGivenLatin" }),
      dataIndex: ["nameGivenLatin"],
      width: 150,
    },
    {
      key: PersonTableColumns.NameFamilyLocal,
      title: formatMessage({ id: "persons.table.column.nameFamilyLocal" }),
      dataIndex: ["nameFamilyLocal"],
      width: 150,
    },
    {
      key: PersonTableColumns.NameFamilyLatin,
      title: formatMessage({ id: "persons.table.column.nameFamilyLatin" }),
      dataIndex: ["nameFamilyLatin"],
      width: 150,
    },
    {
      key: PersonTableColumns.NameAbbreviated,
      title: formatMessage({ id: "persons.table.column.nameAbbreviated" }),
      dataIndex: ["nameAbbreviated"],
      width: 150,
    },
    {
      key: PersonTableColumns.Gender,
      title: formatMessage({ id: "persons.table.column.gender" }),
      dataIndex: "gender",
      width: 50,
    },
    {
      key: PersonTableColumns.Nationality,
      title: formatMessage({ id: "persons.table.column.nationality" }),
      dataIndex: "nationality",
      width: 30,
    },
    {
      key: PersonTableColumns.LanguageLocal,
      title: formatMessage({ id: "persons.table.column.languageLocal" }),
      dataIndex: "languageLocal",
      width: 95,
    },
    {
      key: PersonTableColumns.Dob,
      title: formatMessage({ id: "persons.table.column.dob" }),
      dataIndex: "dob",
      width: 90,
    },
    {
      key: PersonTableColumns.Status,
      title: formatMessage({ id: "persons.table.column.status" }),
      dataIndex: "status",
      width: 40,
    },
    {
      key: PersonTableColumns.Deceased,
      title: formatMessage({ id: "persons.table.column.deceased" }),
      dataIndex: "deceased",
      width: 90,
    },
    {
      key: PersonTableColumns.Height,
      title: formatMessage({ id: "persons.table.column.height" }),
      dataIndex: ["additionalDetails", "height"],
      width: 50,
    },
    {
      key: PersonTableColumns.Weight,
      title: formatMessage({ id: "persons.table.column.weight" }),
      dataIndex: ["additionalDetails", "weight"],
      width: 50,
    },
    {
      key: PersonTableColumns.HeightImperial,
      title: formatMessage({ id: "persons.table.column.heightImperial" }),
      dataIndex: ["additionalDetails", "heightImperial"],
      width: 80,
    },
    {
      key: PersonTableColumns.WeightImperial,
      title: formatMessage({ id: "persons.table.column.weightImperial" }),
      dataIndex: ["additionalDetails", "weightImperial"],
      width: 90,
    },
    {
      key: PersonTableColumns.Representation,
      title: formatMessage({ id: "persons.table.column.representation" }),
      dataIndex: ["additionalDetails", "representation"],
      width: 40,
    },
    {
      key: PersonTableColumns.JuniorAssociationLeague,
      title: formatMessage({ id: "persons.table.column.juniorAssociationLeague" }),
      dataIndex: ["additionalDetails", "juniorAssociationLeague"],
      width: 70,
    },
    {
      key: PersonTableColumns.HomeTown,
      title: formatMessage({ id: "persons.table.column.homeTown" }),
      dataIndex: ["additionalDetails", "homeTown"],
      width: 90,
    },
    {
      key: PersonTableColumns.College,
      title: formatMessage({ id: "persons.table.column.college" }),
      dataIndex: ["additionalDetails", "college"],
      width: 70,
    },
    {
      key: PersonTableColumns.CollegeClass,
      title: formatMessage({ id: "persons.table.column.collegeClass" }),
      dataIndex: ["additionalDetails", "collegeClass"],
      width: 90,
    },
    {
      key: PersonTableColumns.School,
      title: formatMessage({ id: "persons.table.column.school" }),
      dataIndex: ["additionalDetails", "school"],
      width: 70,
    },
    {
      key: PersonTableColumns.SchoolClass,
      title: formatMessage({ id: "persons.table.column.schoolClass" }),
      dataIndex: ["additionalDetails", "schoolClass"],
      width: 80,
    },
  ];
};
